<!--
  TaskForm.vue

  The fields for a new task — name, description, assignees, due date, linked
  records — and the create call. Hosts supply the frame and the buttons:
  AddTaskModal wraps it in a modal; a list block shows it in place of its list.

  Exposes `submit()` (resolves the created record, or null if it didn't
  create) and `canSubmit`. Emits `created` with the new record.
-->
<template>
  <div class="task-form">
    <div class="field">
      <label class="label is-small" for="task-form-name">Name</label>
      <input
        id="task-form-name"
        ref="nameInput"
        v-model="form.name"
        class="input"
        type="text"
        placeholder="What needs to be done?"
        @keydown.enter.prevent="submit"
      />
    </div>

    <div class="field">
      <label class="label is-small" for="task-form-description">Description</label>
      <textarea
        id="task-form-description"
        v-model="form.description"
        class="input"
        rows="2"
        placeholder="Optional details..."
      />
    </div>

    <div class="field">
      <label class="label is-small">Assigned to</label>
      <TaskAssigneePicker v-model="form.assignedTo" :users="getActiveUsers()" />
    </div>

    <div class="field">
      <label class="label is-small" for="task-form-due">Due Date</label>
      <input id="task-form-due" v-model="form.dueDate" class="input" type="date" />
    </div>

    <div class="field">
      <label class="label is-small">Linked Records</label>
      <TaskRelatedRecordsField
        :records="linkedRecords"
        @add="addLinkedRecord"
        @remove="removeLinkedRecord"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, type PropType, ref } from "vue"
import { useRoute } from "vue-router"
import { toast } from "vue-sonner"
import type { BaseRecordFragment, RecordFieldsFragment } from "@auger/core/gql/types"
import { searchRecordsForTaskLink } from "@/common/hasura/records"
import TaskAssigneePicker from "@/components/tasks/TaskAssigneePicker.vue"
import TaskRelatedRecordsField, {
  type RelatedRecord,
} from "@/components/tasks/TaskRelatedRecordsField.vue"
import { createTask } from "@/composables/useTask"
import { client } from "@/init/apollo"
import { getUserRecordId } from "@/stores/user"
import { getActiveUsers } from "@/stores/users"
import { errorMessage } from "@/utils/errors"

const props = defineProps({
  /** Record to pre-link. Falls back to the current route's record when null. */
  relatedRecord: { type: Object as PropType<Partial<BaseRecordFragment> | null>, default: null },
  /** What a new task starts with: a name, a description, and the records it is
   *  linked to (instead of the route's record). */
  initial: {
    type: Object as PropType<{
      name?: string
      description?: string
      related?: RelatedRecord[]
    } | null>,
    default: null,
  },
})

const emit = defineEmits<{ created: [record: RecordFieldsFragment] }>()

const route = useRoute()

// Falls back to the current route's record when the caller doesn't pass one.
const effectiveRelated = computed<Partial<BaseRecordFragment> | null>(() => {
  if (props.relatedRecord) return props.relatedRecord
  const { recordType, slug } = route.params
  if (route.name !== "record-show" || !recordType || !slug) return null
  return { type: String(recordType), slug: String(slug), name: String(slug).replace(/-/g, " ") }
})

const nameInput = ref<HTMLInputElement | null>(null)
const submitting = ref(false)
const linkedRecords = ref<RelatedRecord[]>([...(props.initial?.related ?? [])])

// New tasks start assigned to the current user (empty if unknown).
const me = getUserRecordId()
const form = ref({
  name: props.initial?.name ?? "",
  description: props.initial?.description ?? "",
  assignedTo: (me ? [me] : []) as number[],
  dueDate: "",
})

const canSubmit = computed(() => !!form.value.name.trim() && !submitting.value)

onMounted(() => {
  const related = props.initial?.related?.length ? null : effectiveRelated.value
  if (related?.id) {
    linkedRecords.value = [{ ...related, id: related.id }]
  } else if (related?.slug) {
    // A route-derived record carries type + slug; resolve its id.
    client
      .query({
        query: searchRecordsForTaskLink,
        variables: { where: { slug: { _eq: related.slug }, type: { _eq: related.type } } },
      })
      .then(({ data }) => {
        if (data.records?.[0]) linkedRecords.value = [data.records[0]]
      })
      .catch((err) => {
        console.error("Failed to resolve related record:", err)
        toast.warning("Could not pre-link the related record. You can add it manually.")
      })
  }
  nextTick(() => nameInput.value?.focus())
})

function addLinkedRecord(record: RelatedRecord) {
  if (!record || linkedRecords.value.some((r) => r.id === record.id)) return
  linkedRecords.value = [...linkedRecords.value, record]
}

function removeLinkedRecord(id: number) {
  linkedRecords.value = linkedRecords.value.filter((r) => r.id !== id)
}

async function submit() {
  if (!canSubmit.value) return null
  submitting.value = true
  try {
    const record = await createTask(
      {
        name: form.value.name,
        description: form.value.description,
        dueDate: form.value.dueDate,
        assigneeIds: form.value.assignedTo,
        relatedIds: linkedRecords.value.map((r) => r.id),
      },
      { client }
    )
    toast.success(`Created task: ${record.name}`)
    emit("created", record)
    return record
  } catch (err) {
    const error = errorMessage(err)
    toast.error(`Failed to create task: ${error}`)
    return null
  } finally {
    submitting.value = false
  }
}

defineExpose({ submit, canSubmit, submitting })
</script>

<style>
.task-form {
  .field {
    margin-bottom: 0.75rem;
  }

  .label {
    font-weight: 600;
    margin-bottom: 0.25rem;
  }
}
</style>
